/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import type { StrapiBigFeatures } from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { Spacing } from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Icon } from "@/ui/atoms/icon";
import { Text } from "@/ui/atoms/text";

import { buildStylesByBreakpoint } from "@/util/style_util";
import { getSectionGap } from "@/util/tokens/layout_util";

export function BigFeatures(props: StrapiBigFeatures) {
    /**
     * Styles
     */
    const listStyles = css({
        rowGap: Spacing["spacing-4"],
    });

    const itemStyles = css(
        {
            background: "center center no-repeat",
            backgroundSize: "cover",
            borderRadius: BorderRadiuses["borderLarge"],
            overflow: "hidden",
            position: "relative",
        },
        buildStylesByBreakpoint("gridColumn", {
            extraSmall: "1 / -1",
            medium: "span 4",
        }),
        buildStylesByBreakpoint("padding", {
            extraSmall: Spacing["spacing-6"],
            medium: Spacing["spacing-8"],
            large: Spacing["spacing-12"],
            extraLarge: Spacing["spacing-16"],
        }),
    );

    const itemContentStyles = css({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        zIndex: 1,
    });

    const itemIconStyles = css(
        {
            color: Colors["lighten-50"],
            display: "block",
            marginTop: "auto",
        },
        buildStylesByBreakpoint("paddingTop", {
            extraSmall: Spacing["spacing-4"],
            medium: Spacing["spacing-8"],
            large: Spacing["spacing-10"],
            extraLarge: Spacing["spacing-12"],
        }),
    );

    return (
        <GridContainer
            className={listStyles}
            legacyGrid={false}
            marginBottom={getSectionGap(props.Section_Gap)}
        >
            {props.Items.map((item) => (
                <GridColumn
                    className={css(itemStyles, {
                        backgroundImage: `url(${item.Background?.Media.url})`,
                    })}
                    key={`big-feature-${item.id}`}
                >
                    <div css={itemContentStyles}>
                        <Text
                            fontSize="BigStatsDescription"
                            themeKey="headlinePrimary"
                        >
                            {item.Feature}
                        </Text>

                        <Icon
                            className={itemIconStyles}
                            size={{
                                extraSmall: 36,
                                medium: 40,
                                large: 44,
                                extraLarge: 48,
                            }}
                            slug="Code"
                        />
                    </div>
                </GridColumn>
            ))}
        </GridContainer>
    );
}
